import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Flex, Box } from 'reflexbox';
import WhatStudentSayBackgroundSVG from './images/WhatStudentSayBackground.svg';
import { MARKETING_PAGE_BREAKPOINT_DESKTOP } from '../../../../components/Style/breakpoints';
import { ConnectedCourseCard } from '../../../../components/Marketing/CourseCard/connector';
import { Testimonial } from './types';

const Heading = styled.h2`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 900;
  font-size: 30;
  line-height: 120%;
  letter-spacing: -0.01em;
  color: #041416;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    font-family: Avenir, sans-serif;
    font-style: normal;
    font-weight: 900;
    font-size: 30px;
    margin-bottom: 46px;
  }
`;

const HeadingContainer = styled(Flex)`
  position: relative;
  flex-direction: row;
  max-width: 1070px;
  width: 100%;
  align-items: center;
`;

const TestimonialContainer = styled(Flex)`
  position: relative;
  background: #e5eaeb;
  border-radius: 30px;
  flex-direction: row;
  max-width: 1070px;
  width: 100%;
  padding: 40px 30px 30px 30px;
`;

const CourseCardContainer = styled(Box)`
  display: none;
  @media only screen and (min-width: 991px) {
    display: block;
    position: absolute;
    max-width: 311px;
    z-index: 5;
    right: 100px;
    top: 50px;
  }
`;

const StudentContainer = styled(Flex)`
  position: relative;
  z-index: 2;
  flex-direction: column;
  max-width: 1070px;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    // width: 1013px;
    padding: 70px 70px 50px 50px;
  }
`;

const Description = styled.div`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 140%;
  color: #041416;
  margin-bottom: 20px;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    width: 460px;
    font-size: 22px;
    color: #3e4b4c;
    margin-bottom: 40px;
  }
`;

const Container = styled(Flex)`
  padding: 12px;
  margin: 60px 0;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    justify-content: center;
    margin: 100px 0 200px 0;
  }
`;

const ImageContainer = styled(Box)`
  padding: 12px;
  position: absolute;
  z-index: -1;
  top: -10px;
  left: -80px;
  img {
    width: 300px;
    height: 300px;
  }
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    top: -48px;
    left: -138px;
    z-index: -1;
    img {
      width: 100%;
      height: 100%;
    }
  }
`;

const StudentsList = styled(Flex)`
  margin-left: auto;
  margin-top: 24px;
  margin-bottom: 30px;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    margin: 0;
    position: absolute;
    right: 0;
  }
`;

const Content = styled(Flex)`
  flex-direction: column;
  position: relative;
  width: 100%;
  align-items: center;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
  }
`;

interface StudentButtonProps {
  active?: boolean;
}

const StudentButton = styled('button')<StudentButtonProps>(
  ({ active = false }) => ({
    width: 40,
    height: 40,
    padding: 0,
    display: 'block',
    flexShrink: 0,
    margin: '0 3px',
    borderRadius: '50%',
    border: active ? '4px solid #cf1b53' : '4px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    overflow: 'hidden',

    '&:hover': {
      border: '4px solid #cf1b53',
    },

    [`@media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP})`]: {
      width: 80,
      height: 80,
    },
  })
);

interface StudentButtonImgProps {
  active?: boolean;
}

const StudentButtonImg = styled('img')<StudentButtonImgProps>(
  ({ active = false }) => ({
    width: '100%',
    opacity: active ? 1 : 0.7,
  })
);

const StudentName = styled(Box)({
  color: '#041416',
});

const Cohort = styled(Box)({
  color: '#3E4B4C',
});

const Avatar = styled('img')({
  width: 80,
  overflow: 'hidden',
  borderRadius: '50%',
  border: '4px solid #ffffff',
});

export const WhatStudentSay = React.memo(
  ({ testimonials }: { testimonials: Testimonial[] }) => {
    const [currentTestimonialIndex, setTestimonial] = useState<number>(0);

    const currentTestimonial = testimonials[currentTestimonialIndex];
    const { name, avatar, cohort, description, course } = currentTestimonial;

    return (
      <Container>
        <Content>
          <HeadingContainer>
            <Heading>Real world impact from online courses</Heading>
            <StudentsList>
              {testimonials.map(
                ({ avatar: itemImg, name: itemName }, index) => (
                  <StudentButton
                    onClick={() => setTestimonial(index)}
                    active={index === currentTestimonialIndex}
                    key={itemName}
                  >
                    <StudentButtonImg
                      src={itemImg}
                      alt={itemName}
                      active={index === currentTestimonialIndex}
                    />
                  </StudentButton>
                )
              )}
            </StudentsList>
          </HeadingContainer>

          <TestimonialContainer>
            <CourseCardContainer>
              <ConnectedCourseCard {...course} />
            </CourseCardContainer>

            <StudentContainer>
              <Description>{description}</Description>
              <Flex>
                <Avatar src={avatar} alt="WhatStudentSay_student1" />
                <Flex flexDirection="column" ml={12} justifyContent="center">
                  <StudentName
                    fontFamily="Avenir"
                    fontStyle="normal"
                    fontWeight="900"
                    fontSize="22px"
                    lineHeight="120%"
                  >
                    {name}
                  </StudentName>
                  <Cohort
                    fontFamily="Avenir"
                    fontStyle="normal"
                    fontWeight="normal"
                    fontSize="16px"
                    lineHeight="140%"
                  >
                    {cohort}
                  </Cohort>
                </Flex>
              </Flex>
            </StudentContainer>

            <ImageContainer>
              <img src={WhatStudentSayBackgroundSVG} alt="" />
            </ImageContainer>
          </TestimonialContainer>
        </Content>
      </Container>
    );
  }
);
